import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  SortOrderDropDownSelection,
} from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import useThemeColor from "../../hooks/useThemeColor";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import "./../order/ManageOrderFilters.css";
import CustomModal from "../../components/CustomComponents/modal/customModal";

const ManageItems = () => {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const { RootCat_ID } = useParams();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.auth.user);
  const assignedBy = user?.userData?.email || "";
  const userIdentity = user?.userData?.userId ?? user?.userData?.email ?? CompID;


  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();


  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [originalItemsCount, setOriginalItemsCount] = useState(0);
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [companyProfile, setCompanyProfile] = useState(null);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideItem, setOverrideItem] = useState(null);
  const [overrideForm, setOverrideForm] = useState({
    branchPublishMode: "inherit", // inherit | publish | unpublish
    branchOrderby: "",
    isPriceOverrideActive: false,
    branchPriceOverride: "",
    branchDeliveryPriceOverride: "",
    branchTakeAwayPriceOverride: "",
  });
  const lastSelectedIndex = useRef(null);
  const [categoryOptions, setCategoryOptions] = useState([{ value: "", label: "All Categories" }]);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const preferencesInitialized = useRef(false);

  const toInt = (v) => {
    if (v === null || v === undefined) return 0;
    const n = typeof v === "number" ? v : parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  const parentCompId = toInt(companyProfile?.parentComID ?? companyProfile?.ParentComID);
  const isBranchMode = parentCompId > 0;
  const isMain = !isBranchMode;

  const pageKey = "items";
  const defaultVisibleColumns = {
    title: true,
    sku: true,
    Price: true,
    deliveryPrice: false,
    takeAwayPrice: false,
    Reporting: false,
    sortOrder: true,
    status: true,
    rating: false,
    currentStock: true
  };

  const defaultSortOrder = { value: "DESC", label: "DESC" };
  const defaultSortBy = { value: "MenuItem_Orderby", label: "Sort Order" };

  // Filter options
  const defaultStatus = { value: "", label: "All Status" };
  const defaultCategory = { value: "", label: "All Categories" };
  const defaultMenuType = { value: "", label: "All Items" };

  const parentIdForBranch = isMain && CompID != null ? (typeof CompID === "number" ? CompID : parseInt(CompID, 10)) : null;
  const companyName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
  const defaultBranch = isMain && parentIdForBranch != null && !isNaN(parentIdForBranch)
    ? { value: parentIdForBranch, label: companyName }
    : { value: "", label: "All Branches" };

  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;

  // Filter states
  const status = preferences.status || defaultStatus;
  const category = preferences.category || defaultCategory;
  const menuType = preferences.menuType || defaultMenuType;
  const branch = preferences.branch || defaultBranch;
  const archiveStatus = preferences.archiveStatus || "active";
  const isArchivedView = archiveStatus === "archived";
  const isBranchSelected = isMain && branch?.value && branch.value !== parentIdForBranch;
  const showArchiveTabs = isMain && !isBranchMode && !isBranchSelected;

  const sortByOptions = [
    { value: "MenuItem_Orderby", label: "Sort Order" },
    { value: "MenuItem_ID", label: "ID" },
    { value: "MenuItem_Name", label: "Name" },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "InActive" },
  ];

  const menuTypeOptions = [
    { value: "", label: "All Items" },
    { value: "SellingItems", label: "Selling Items" },
    { value: "NonSellingItems", label: "Non Selling Items" },
    { value: "Modifier", label: "Add-ons Items" },
    { value: "RecipeItems", label: "Recipe Items" },
    { value: "NonRecipeItems", label: "Non-Recipe Items" },
  ];

  const branchOptions = locations.length > 0
    ? locations
    : parentIdForBranch != null && !isNaN(parentIdForBranch)
      ? [{ value: parentIdForBranch, label: companyName }]
      : [];

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Fetch CompCurrency
  const fetchCompCurrency = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_COMPANY_PROFILE);
      const profileData = response.data.data;
      setCompanyProfile(profileData || null);
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, []);

  // Fetch locations (branches) for parent company
  const fetchLocations = useCallback(async () => {
    if (!isMain || !CompID) return;
    try {
      setLoadingLocations(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID },
      });
      const locationData = response.data?.data ?? response.data ?? [];
      const arr = Array.isArray(locationData) ? locationData : [];
      const parentId = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
      const compName = profileData?.compName ?? profileData?.CompName ?? "Parent Branch";
      const base = [{ value: parentId, label: compName }];
      const branchOpts = arr
        .filter((loc) => {
          const pid = loc.ParentComID ?? loc.parentComID;
          const id = loc.CompID ?? loc.compID;
          const pidNum = pid != null ? (typeof pid === "number" ? pid : parseInt(pid, 10)) : null;
          const idNum = id != null ? (typeof id === "number" ? id : parseInt(id, 10)) : null;
          if (idNum == null || isNaN(idNum)) return false;
          if (pidNum === parentId && idNum !== parentId) return true;
          return false;
        })
        .map((loc) => {
          const id = loc.CompID ?? loc.compID;
          const name = String(loc.CompName ?? loc.compName ?? "").trim() || `Branch ${id ?? ""}`;
          const val = typeof id === "number" ? id : parseInt(id, 10);
          return { value: isNaN(val) ? null : val, label: name };
        })
        .filter((o) => o.value != null);
      setLocations([...base, ...branchOpts]);
    } catch (err) {
      showErrorToast("Failed to load branches");
    } finally {
      setLoadingLocations(false);
    }
  }, [CompID, isMain, profileData?.compName, profileData?.CompName]);

  // Fetch Categories
  const fetchCategories = useCallback(async () => {
    try {
      const effectiveCompID = isBranchMode ? parentCompId : (branch?.value && branch.value !== "" ? branch.value : CompID);
      const response = await axiosInstance.get("/api/RootCategories/BO_GetAllCategories", {
        params: {
          CompID: effectiveCompID,
          SortBy: 'rootCat_Orderby',
          SortOrder: 'ASC',
          Search: '',
        }
      });

      if (response.data.status && response.data.data) {
        const categories = transformCategoriesToOptions(response.data.data);
        setCategoryOptions([{ value: "", label: "All Categories" }, ...categories]);
      }
    } catch (err) {
      showErrorToast("Failed to load categories");
    }
  }, [CompID, isBranchMode, parentCompId, branch?.value]);

  // Filter function to exclude add-on categories
  const filterAddOnCategories = (categoriesArray) => {
    return categoriesArray
      .map((category) => {
        // Skip add-on categories
        if (category && category.isAddOnCategory === true) {
          return null;
        }

        // Recursively filter subcategories
        let filteredSubCategories = [];
        if (
          category &&
          category.subCategoriesList &&
          category.subCategoriesList.length > 0
        ) {
          filteredSubCategories = filterAddOnCategories(category.subCategoriesList);
        }

        if (!category) return null;

        return {
          ...category,
          subCategoriesList: filteredSubCategories,
        };
      })
      .filter(Boolean);
  };

  // Transform hierarchical categories to flat list with parent-child indication
  const transformCategoriesToOptions = (categories) => {
    const options = [];

    // First filter out add-on categories
    const categoriesWithoutAddOn = filterAddOnCategories(categories);

    categoriesWithoutAddOn.forEach(category => {
      // Add parent category
      options.push({
        value: category.rootCat_ID,
        label: category.rootCat_Name
      });

      // Add subcategories with indentation
      if (category.subCategoriesList && category.subCategoriesList.length > 0) {
        category.subCategoriesList.forEach(subCategory => {
          options.push({
            value: subCategory.rootCat_ID,
            label: `  ↳ ${subCategory.rootCat_Name}`
          });
        });
      }
    });

    return options;
  };

  useEffect(() => {
    // Prevent infinite loop - only initialize once
    if (preferencesInitialized.current) return;
    
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }

    // Initialize filter states
    if (!preferences.status) {
      dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    }
    if (!preferences.category) {
      dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    }
    if (!preferences.menuType) {
      dispatch(setFilterField({ pageKey, field: "menuType", value: defaultMenuType }));
    }
    if (isMain && !preferences.branch) {
      dispatch(setFilterField({ pageKey, field: "branch", value: defaultBranch }));
    }
    if (!preferences.archiveStatus) {
      dispatch(setFilterField({ pageKey, field: "archiveStatus", value: "active" }));
    }

    // Mark as initialized after a short delay to allow dispatches to complete
    const timer = setTimeout(() => {
      preferencesInitialized.current = true;
    }, 50);

    return () => clearTimeout(timer);
  }, [dispatch, pageKey]);

  // When user/account changes (login, logout+login as different account), reset branch to current
  // user's location and refetch profile/locations so data loads without manual refresh
  useEffect(() => {
    if (!CompID) return;
    const compIdNum = typeof CompID === "number" ? CompID : parseInt(CompID, 10);
    if (isNaN(compIdNum)) return;
    const label = profileData?.compName ?? profileData?.CompName ?? "Company";
    dispatch(setFilterField({ pageKey, field: "branch", value: { value: compIdNum, label } }));
    fetchCompCurrency();
    if (isMain) fetchLocations();
  }, [userIdentity, CompID, profileData?.compName, profileData?.CompName, isMain, dispatch, pageKey]);

  // Fetch CompCurrency, Categories, and Locations on component mount
  useEffect(() => {
    fetchCompCurrency();
    fetchCategories();
    if (isMain) fetchLocations();
  }, [fetchCompCurrency, fetchCategories, fetchLocations, isMain]);

  const fetchItemsData = useCallback(async () => {
    try {
      setLoading(true);
      const isBranchSelected = isMain && branch?.value && branch.value !== "" && branch.value !== parentIdForBranch;
      if (isBranchMode || isBranchSelected) {
        const branchCompId = isBranchMode ? CompID : branch.value;
        const parentId = isBranchMode ? parentCompId : CompID;
        const response = await axiosInstance.get(APIs.GET_PRODUCTS_BY_BRANCH, {
          params: {
            branchCompId,
            parentCompId: parentId,
            pageNumber,
            pageSize: pageSize.value,
            search: search?.trim() || "",
            sortBy: sortBy.value,
            sortOrder: sortOrder.value,
            rootCatId: category?.value || RootCat_ID || 0,
            status: status?.value !== "" ? String(status?.value) : "",
            menuType: menuType?.value !== "" ? menuType.value : ""
          },
        });

        const payload = response.data?.data;
        const branchItems = payload?.menuItems || payload?.MenuItems || [];

        setTotalPages(payload?.pageCount || payload?.PageCount || 0);
        setTotalRecords(payload?.totalRecord || payload?.TotalRecord || 0);

        if (Array.isArray(branchItems)) {
          setItems(branchItems);
          if (!search.trim()) setOriginalItemsCount(branchItems.length);
        } else {
          setItems([]);
        }
      } else {
        const response = await axiosInstance.get(APIs.GET_ALL_ITEMS_FOR_GRID, {
          params: {
            CompID,
            SortBy: sortBy.value,
            SortOrder: sortOrder.value,
            Search: search?.trim() || "",
            PageNumber: pageNumber,
            PageSize: pageSize.value,
            RootCat_ID: category?.value || RootCat_ID || 0,
            Status: status?.value !== "" ? String(status?.value) : "",
            MenuType: menuType?.value !== "" ? menuType.value : "",
            ArchiveStatus: isArchivedView ? "archived" : "active",
          },
        });

        const itemsData = response.data.data || response.data;

        setTotalPages(response.data.totalPages || 0);
        setTotalRecords(response.data.totalRecords || 0);
        if (Array.isArray(itemsData)) {
          setItems(itemsData);

          if (!search.trim()) {
            setOriginalItemsCount(itemsData.length);
          }
        } else {
          setItems([]);
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setItems([]);
    }
  }, [search, sortBy, sortOrder, CompID, pageNumber, pageSize, status, category, menuType, RootCat_ID, isBranchMode, isMain, parentCompId, branch, parentIdForBranch, isArchivedView, isBranchSelected]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchItemsData();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchItemsData]);


  const handleDeleteItem = async (item) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: isBranchMode
          ? `Remove ${item.menuItem_Name} from this branch?`
          : `Are you sure you want to archive ${item.menuItem_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = isBranchMode
        ? await axiosInstance.delete(APIs.DELETE_UNASSIGN_PRODUCT_FROM_BRANCH, {
          params: {
            masterMenuItemId: item.menuItem_ID,
            parentCompId,
            branchCompId: CompID,
            forceUnassign: false,
          },
        })
        : await axiosInstance.delete(APIs.DELETE_ITEM, {
          params: {
            CompID,
            MenuItem_ID: item.menuItem_ID,
          },
        });

      if (response.data.status) {
        setItems((prevItems) =>
          prevItems.filter((i) => i.menuItem_ID !== item.menuItem_ID)
        );
        showSuccessToast(isBranchMode ? "Removed from branch." : "Item archived successfully.");
      } else {
        showErrorToast(response.data.message || (isBranchMode ? "Remove failed" : "Deletion failed"));
      }
      setLoading(false);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleRestoreItem = async (item) => {
    try {
      const confirmRestore = await swal({
        title: "Restore item?",
        text: `Restore ${item.menuItem_Name} to active products?`,
        icon: "info",
        buttons: true,
      });

      if (!confirmRestore) return;

      setLoading(true);
      const response = await axiosInstance.put(APIs.PUT_RESTORE_ITEM, null, {
        params: {
          CompID,
          MenuItem_ID: item.menuItem_ID,
        },
      });

      if (response.data.status) {
        setItems((prevItems) => prevItems.filter((i) => i.menuItem_ID !== item.menuItem_ID));
        showSuccessToast("Item restored successfully.");
      } else {
        showErrorToast(response.data.message || "Restore failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something Went Wrong.");
      setLoading(false);
    }
  };

  const handlePermanentDeleteItem = async (item) => {
    try {
      const confirmDelete = await swal({
        title: "Permanently delete?",
        text: `This will permanently delete ${item.menuItem_Name}. This cannot be undone.`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_PERMANENT_ITEM, {
        params: {
          CompID,
          MenuItem_ID: item.menuItem_ID,
        },
      });

      if (response.data.status) {
        setItems((prevItems) => prevItems.filter((i) => i.menuItem_ID !== item.menuItem_ID));
        showSuccessToast("Item permanently deleted.");
      } else {
        showErrorToast(response.data.message || "Permanent delete failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something Went Wrong.");
      setLoading(false);
    }
  };

  const handleSelectItem = (itemId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);

        for (let i = start; i <= end; i++) {
          if (items[i]?.menuItem_ID) {
            newSelected.add(items[i].menuItem_ID);
          }
        }
      } else {
        if (newSelected.has(itemId)) {
          newSelected.delete(itemId);
        } else {
          newSelected.add(itemId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(items.map((item) => item.menuItem_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: isBranchMode
          ? `Remove ${selectedItems.size} selected items from this branch?`
          : `Are you sure you want to archive ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      if (isBranchMode) {
        const results = await Promise.allSettled(
          Array.from(selectedItems).map((itemId) =>
            axiosInstance.delete(APIs.DELETE_UNASSIGN_PRODUCT_FROM_BRANCH, {
              params: {
                masterMenuItemId: itemId,
                parentCompId,
                branchCompId: CompID,
                forceUnassign: false,
              },
            })
          )
        );

        const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.data?.status));
        if (failed.length > 0) {
          showErrorToast(`${failed.length} item(s) could not be removed (likely due to stock).`);
        } else {
          showSuccessToast(`${selectedItems.size} items removed from branch successfully.`);
        }
      } else {
        await Promise.all(
          Array.from(selectedItems).map((itemId) =>
            axiosInstance.delete(APIs.DELETE_ITEM, {
              params: {
                CompID,
                MenuItem_ID: itemId,
              },
            })
          )
        );
        showSuccessToast(`${selectedItems.size} items archived successfully.`);
      }

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchItemsData();
      setLoading(false);
    } catch (err) {
      showErrorToast(isBranchMode ? "Failed to remove selected items." : "Failed to delete selected items.");
      setLoading(false);
    }
  };

  const handleBulkRestore = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for restore.");
      return;
    }

    try {
      const confirmRestore = await swal({
        title: "Restore selected items?",
        text: `Restore ${selectedItems.size} selected archived items?`,
        icon: "info",
        buttons: true,
      });

      if (!confirmRestore) return;

      setLoading(true);
      const results = await Promise.allSettled(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.put(APIs.PUT_RESTORE_ITEM, null, {
            params: { CompID, MenuItem_ID: itemId },
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.data?.status));
      if (failed.length > 0) {
        showErrorToast(`${failed.length} item(s) could not be restored.`);
      } else {
        showSuccessToast(`${selectedItems.size} items restored successfully.`);
      }

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchItemsData();
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to restore selected items.");
      setLoading(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for permanent delete.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Permanently delete?",
        text: `Permanently delete ${selectedItems.size} selected items? This cannot be undone.`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const results = await Promise.allSettled(
        Array.from(selectedItems).map((itemId) =>
          axiosInstance.delete(APIs.DELETE_PERMANENT_ITEM, {
            params: { CompID, MenuItem_ID: itemId },
          })
        )
      );

      const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.data?.status));
      if (failed.length > 0) {
        showErrorToast(`${failed.length} item(s) could not be permanently deleted.`);
      } else {
        showSuccessToast(`${selectedItems.size} items permanently deleted.`);
      }

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchItemsData();
      setLoading(false);
    } catch (err) {
      showErrorToast("Failed to permanently delete selected items.");
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for editing.");
      return;
    }
    if (isBranchMode) {
      showErrorToast("Bulk edit is not available for branches. Use branch overrides instead.");
      return;
    }

    const selectedItemIds = Array.from(selectedItems);
    navigate(`/items/bulkEditItems`, { state: { selectedItemIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No items selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: isBranchMode
          ? `Are you sure you want to ${publishStatus ? "publish" : "unpublish"} ${selectedItems.size} selected items for this branch?`
          : `Are you sure you want to ${publishStatus ? "publish" : "unpublish"} ${selectedItems.size} selected items?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedItems = items.map((item) =>
        selectedItems.has(item.menuItem_ID)
          ? { ...item, menuItem_Publish: publishStatus }
          : item
      );
      setItems(updatedItems);

      if (isBranchMode) {
        const selectedRows = items.filter((i) => selectedItems.has(i.menuItem_ID));
        const results = await Promise.allSettled(
          selectedRows.map((row) => {
            if (!row?.branchAssignmentID) {
              return Promise.reject(new Error("Missing branch assignment for one or more items."));
            }

            return axiosInstance.put(APIs.PUT_UPDATE_BRANCH_ASSIGNMENT, {
              ID: row.branchAssignmentID,
              MasterMenuItem_ID: row.menuItem_ID,
              MasterCompID: parentCompId,
              BranchCompID: CompID,
              IsAssigned: true,
              IsActive: row.branchAssignmentIsActive ?? true,
              IsPriceOverrideActive: row.branchIsPriceOverrideActive ?? false,
              BranchPriceOverride: row.branchPriceOverride ?? null,
              BranchDeliveryPriceOverride: row.branchDeliveryPriceOverride ?? null,
              BranchTakeAwayPriceOverride: row.branchTakeAwayPriceOverride ?? null,
              BranchPublish: publishStatus,
              BranchOrderby: row.branchOrderby ?? null,
            });
          })
        );

        const failed = results.filter((r) => r.status === "rejected" || (r.status === "fulfilled" && !r.value?.data?.status));
        if (failed.length > 0) {
          showErrorToast(`${failed.length} item(s) status could not be updated.`);
        } else {
          showSuccessToast(`${selectedItems.size} items status updated successfully.`);
        }
      } else {
        await Promise.all(
          Array.from(selectedItems).map((itemId) =>
            axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
              CompID,
              MenuItem_ID: itemId,
              MenuItem_Publish: publishStatus,
            })
          )
        );
      }

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      setLoading(false);
    } catch (error) {
      showErrorToast("Failed to update selected items status.");
      fetchItemsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    dispatch(setFilterField({ pageKey, field: "status", value: defaultStatus }));
    dispatch(setFilterField({ pageKey, field: "category", value: defaultCategory }));
    dispatch(setFilterField({ pageKey, field: "archiveStatus", value: "active" }));
    if (isMain) dispatch(setFilterField({ pageKey, field: "branch", value: defaultBranch }));
    setPageNumber(1);
  };

  const handleDragStart = (e, item) => {
    if (isBranchMode) return;
    const index = items.findIndex((i) => i.menuItem_ID === item.menuItem_ID);
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (isBranchMode) return;

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(overIndex, 0, draggedItem);
    newItems = newItems.map((item, index) => ({
      ...item,
      menuItem_Orderby: index + 1 + (pageNumber - 1) * pageSize.value,
    }));

    setItems(newItems);

    try {
      await axiosInstance.post(APIs.POST_UPDATE_ITEM_SORT_ORDER, {
        CompID: CompID,
        Updates: newItems.map((item) => ({
          MenuItem_ID: item.menuItem_ID,
          MenuItem_Orderby: item.menuItem_Orderby,
        })),
      });
      fetchItemsData();
    } catch (error) {
      showErrorToast("Failed to update item order.");
      fetchItemsData();
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const toggleItemStatus = async (item, currentStatus) => {
    const menuItem_ID = item?.menuItem_ID;
    try {
      const updatedItems = items.map((row) =>
        row.menuItem_ID === menuItem_ID
          ? { ...row, menuItem_Publish: !currentStatus }
          : row
      );
      setItems(updatedItems);

      if (isBranchMode) {
        if (!item?.branchAssignmentID) {
          throw new Error("Branch assignment not found for this item.");
        }

        await axiosInstance.put(APIs.PUT_UPDATE_BRANCH_ASSIGNMENT, {
          ID: item.branchAssignmentID,
          MasterMenuItem_ID: menuItem_ID,
          MasterCompID: parentCompId,
          BranchCompID: CompID,
          IsAssigned: true,
          IsActive: item.branchAssignmentIsActive ?? true,
          IsPriceOverrideActive: item.branchIsPriceOverrideActive ?? false,
          BranchPriceOverride: item.branchPriceOverride ?? null,
          BranchDeliveryPriceOverride: item.branchDeliveryPriceOverride ?? null,
          BranchTakeAwayPriceOverride: item.branchTakeAwayPriceOverride ?? null,
          BranchPublish: !currentStatus,
          BranchOrderby: item.branchOrderby ?? null,
        });
      } else {
        await axiosInstance.post(APIs.POST_UPDATE_ITEM_STATUS, {
          CompID: CompID,
          MenuItem_ID: menuItem_ID,
          MenuItem_Publish: !currentStatus,
        });
      }
    } catch (error) {
      const revertedItems = items.map((row) =>
        row.menuItem_ID === menuItem_ID
          ? { ...row, menuItem_Publish: currentStatus }
          : row
      );
      setItems(revertedItems);
      showErrorToast(error?.response?.data?.message || error?.message || "Failed to update item status.");
    }
  };

  const openOverrideModalForItem = (item) => {
    if (!item) return;

    const publishMode =
      item.branchPublish === null || item.branchPublish === undefined
        ? "inherit"
        : item.branchPublish
          ? "publish"
          : "unpublish";

    setOverrideItem(item);
    setOverrideForm({
      branchPublishMode: publishMode,
      branchOrderby: item.branchOrderby ?? "",
      isPriceOverrideActive: item.branchIsPriceOverrideActive ?? false,
      branchPriceOverride: item.branchPriceOverride ?? "",
      branchDeliveryPriceOverride: item.branchDeliveryPriceOverride ?? "",
      branchTakeAwayPriceOverride: item.branchTakeAwayPriceOverride ?? "",
    });
    setOverrideModalOpen(true);
  };

  const closeOverrideModal = () => {
    setOverrideModalOpen(false);
    setOverrideItem(null);
  };

  const saveBranchOverrides = async () => {
    if (!isBranchMode) return;
    if (!overrideItem?.branchAssignmentID) {
      showErrorToast("Branch assignment not found for this item.");
      return;
    }

    const isPublishInherit = overrideForm.branchPublishMode === "inherit";
    const publishOverride = isPublishInherit ? null : overrideForm.branchPublishMode === "publish";

    const isOrderInherit =
      overrideForm.branchOrderby === "" || overrideForm.branchOrderby === null || overrideForm.branchOrderby === undefined;
    const orderOverride = isOrderInherit ? null : parseInt(overrideForm.branchOrderby, 10);

    const parseNullableFloat = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = typeof v === "number" ? v : parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.PUT_UPDATE_BRANCH_ASSIGNMENT, {
        ID: overrideItem.branchAssignmentID,
        MasterMenuItem_ID: overrideItem.menuItem_ID,
        MasterCompID: parentCompId,
        BranchCompID: CompID,
        IsAssigned: true,
        IsActive: overrideItem.branchAssignmentIsActive ?? true,
        IsPriceOverrideActive: !!overrideForm.isPriceOverrideActive,
        BranchPriceOverride: parseNullableFloat(overrideForm.branchPriceOverride),
        BranchDeliveryPriceOverride: parseNullableFloat(overrideForm.branchDeliveryPriceOverride),
        BranchTakeAwayPriceOverride: parseNullableFloat(overrideForm.branchTakeAwayPriceOverride),
        BranchPublish: publishOverride,
        BranchOrderby: Number.isFinite(orderOverride) ? orderOverride : null,
        // NOTE: backend SP doesn't currently persist AssignedBy on updates; kept for future use.
        AssignedBy: assignedBy,

        // Clear flags for true reset to inherit
        ClearBranchPublish: isPublishInherit,
        ClearBranchOrderby: isOrderInherit,
        ClearBranchPriceOverride:
          !overrideForm.isPriceOverrideActive || overrideForm.branchPriceOverride === "" || overrideForm.branchPriceOverride === null,
        ClearBranchDeliveryPriceOverride:
          !overrideForm.isPriceOverrideActive || overrideForm.branchDeliveryPriceOverride === "" || overrideForm.branchDeliveryPriceOverride === null,
        ClearBranchTakeAwayPriceOverride:
          !overrideForm.isPriceOverrideActive || overrideForm.branchTakeAwayPriceOverride === "" || overrideForm.branchTakeAwayPriceOverride === null,
      });

      if (response.data.status) {
        showSuccessToast("Branch overrides updated.");
        closeOverrideModal();
        fetchItemsData();
      } else {
        showErrorToast(response.data.message || "Failed to update branch overrides.");
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Failed to update branch overrides.");
    } finally {
      setLoading(false);
    }
  };

  // Check if any filter is applied
  const hasFiltersApplied = () => {
    return (
      search.trim() !== "" ||
      (status && status.value !== "") ||
      (category && category.value !== "") ||
      (menuType && menuType.value !== "") ||
      (isArchivedView) ||
      (isMain && branch?.value && branch.value !== parentIdForBranch)
    );
  };

  const renderNoDataMessage = () => {
    if (items.length === 0 && hasFiltersApplied()) {
      return (
        <div className="alert alert-info text-center mt-3">
          <i className="fa fa-info-circle me-2"></i>
          {isArchivedView
            ? "No archived products found matching your filter criteria."
            : "No items found matching your filter criteria."}
        </div>
      );
    }
    if (items.length === 0 && isArchivedView) {
      return (
        <div className="alert alert-info text-center mt-3">
          <i className="fa fa-info-circle me-2"></i>
          No archived products found.
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Items Catalog</li>
          </ol>
          <h1 className="page-header mb-0">Items Catalog</h1>
        </div>
        <div className="ms-auto">
          {!isBranchMode && !isArchivedView && (
            <div className="d-flex justify-content-end align-items-center mb-3 gap-2">
              <button
                className="btn px-4"
                onClick={() => navigate('/items/createItem/Food')}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
                Create Item
              </button>

              <button
                className="btn px-4"
                onClick={() => navigate('/items/createItem/Food/Combo')}
                style={{
                  backgroundColor: themeColor,
                  background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.3s ease',
                  boxShadow: `0 2px 8px ${themeColor}40`
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }}
              >
                <i className="fas fa-layer-group fa-lg me-2 ms-n2 text-white"></i>
                Create Combo Item
              </button>
              {/*   
            <button
              className="btn px-4"
              onClick={() => navigate('/items/bulkUploadProducts')}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fas fa-file-excel fa-lg me-2 ms-n2 text-white"></i>
              Bulk Upload
            </button>
            */}
            </div>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        {showArchiveTabs && (
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${!isArchivedView ? " active" : ""}`}
                onClick={() => {
                  dispatch(setFilterField({ pageKey, field: "archiveStatus", value: "active" }));
                  setPageNumber(1);
                  setSelectedItems(new Set());
                }}
              >
                Active
              </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${isArchivedView ? " active" : ""}`}
                onClick={() => {
                  dispatch(setFilterField({ pageKey, field: "archiveStatus", value: "archived" }));
                  setPageNumber(1);
                  setSelectedItems(new Set());
                }}
              >
                Archived
              </button>
            </li>
          </ul>
        )}
        {/* Filter panel always visible (same pattern as manageDiscount) */}
        {!loading && (
          <>
            <div className="card border-0">
              <div className="tab-content p-3">
                <div className="tab-pane fade show active" id="allTab">
                  {/* Enhanced Filter Section */}
                  <div className="mb-4">
                    <div
                      className="card filter-card"
                      style={{
                        position: "relative",
                        zIndex: 1,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: `0 4px 20px ${themeColor}40`
                      }}
                    >
                      <div className="card-body p-3" >
                        {/* Filter Header */}

                        <div className="row g-3 align-items-center">
                          {/* Search Field */}
                          <div className="col-xl-3 col-lg-3 col-md-6">
                            <div className="search-input filter-control">
                              <input
                                type="text"
                                className="filter-input"
                                value={search}
                                onChange={(e) => {
                                  setSearch(e.target.value);
                                  setPageNumber(1);
                                }}
                                placeholder="Search by name, category..."
                                style={{
                                  backgroundColor: isDarkMode ? '#2d3436' : 'rgba(255, 255, 255, 0.9)',
                                  border: isDarkMode ? '1px solid #404040' : '1px solid rgba(255, 255, 255, 0.3)',
                                  color: isDarkMode ? '#ffffff' : '#333',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  outline: 'none'
                                }}
                              />
                            </div>
                          </div>

                          {/* Branch Dropdown - only for parent company with branches */}
                          {isMain && branchOptions.length > 1 && (
                            <div className="col-xl-2 col-lg-2 col-md-6">
                              <div className="filter-control">
                                <Select
                                  value={branch}
                                  onChange={(val) => {
                                    dispatch(setFilterField({ pageKey, field: "branch", value: val }));
                                    setPageNumber(1);
                                  }}
                                  options={branchOptions}
                                  styles={dropdownStyles}
                                  placeholder="Branch"
                                  isLoading={loadingLocations}
                                  isSearchable={false}
                                  menuPortalTarget={document.body}
                                  menuPosition="fixed"
                                  menuShouldBlockScroll={false}
                                  menuShouldScrollIntoView={false}
                                  closeMenuOnScroll={false}
                                />
                              </div>
                            </div>
                          )}

                          {/* Status Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={status}
                                onChange={(val) => {
                                  dispatch(
                                    setFilterField({
                                      pageKey,
                                      field: "status",
                                      value: val,
                                    })
                                  );
                                  setPageNumber(1);
                                }}
                                options={statusOptions}
                                styles={dropdownStyles}
                                placeholder="All Status"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Category Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={category}
                                onChange={(val) => {
                                  dispatch(
                                    setFilterField({
                                      pageKey,
                                      field: "category",
                                      value: val,
                                    })
                                  );
                                  setPageNumber(1);
                                }}
                                options={categoryOptions}
                                styles={dropdownStyles}
                                placeholder="All Categories"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* MenuType Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={menuType}
                                onChange={(val) => {
                                  dispatch(
                                    setFilterField({
                                      pageKey,
                                      field: "menuType",
                                      value: val,
                                    })
                                  );
                                  setPageNumber(1);
                                }}
                                options={menuTypeOptions}
                                styles={dropdownStyles}
                                placeholder="All Items"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Order By Dropdown */}
                          <div className="col-xl-2 col-lg-2 col-md-6">
                            <div className="filter-control">
                              <Select
                                value={sortBy}
                                onChange={(val) =>
                                  dispatch(setSortBy({ pageKey, sortBy: val }))
                                }
                                options={sortByOptions}
                                styles={dropdownStyles}
                                placeholder="Order By"
                                className="filter-dropdown"
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                menuShouldBlockScroll={false}
                                menuShouldScrollIntoView={false}
                                closeMenuOnScroll={false}
                              />
                            </div>
                          </div>

                          {/* Sort Order Dropdown */}
                          <div className="col-xl-1 col-lg-1 col-md-6">
                            <div className="filter-control">
                              <SortOrderDropDownSelection
                                defaultValue={sortOrder}
                                onChange={(val) =>
                                  dispatch(setSortOrder({ pageKey, sortOrder: val }))
                                }
                              />
                            </div>
                          </div>

                          {/* Action Buttons - Aligned to Right */}
                          <div className="col-xl-auto col-lg-auto col-md-12 ms-xl-auto">
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                className="clear-btn"
                                onClick={clearFilters}
                                title="Clear all filters"
                              >
                                <i className="fa fa-times me-1"></i>
                                Filter
                              </button>

                              <ColumnSelection
                                visibleColumns={visibleColumns}
                                setVisibleColumns={(cols) =>
                                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                                }
                              />
                            </div>
                          </div>

                        </div>

                      </div>
                    </div>
                  </div>

                  {/* Add Item button - one time, after filter panel (same as manageDiscount) */}
                  {!isBranchMode && !isArchivedView && !loading && originalItemsCount === 0 && (
                    <div className="mb-4">
                      <AddButton
                        onClick={() => navigate('/items/createItem/Food')}
                        title="Add Item"
                        fontSize="16px"
                      />
                    </div>
                  )}

                  {/* Selection Info and Actions (Shown only when items are selected) */}
                  {selectedItems.size > 0 && (
                    <div className="mb-3">
                      <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                        <div className="d-flex align-items-center gap-3">
                          <span className="fw-bold text-primary">
                            {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
                          </span>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleDeselectAll}
                          >
                            Clear Selection
                          </button>
                        </div>

                        <div className="d-flex gap-2">
                          {!isBranchMode && !isArchivedView && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={handleBulkEdit}
                              title="Edit Selected Items"
                            >
                              <i className="fa fa-edit me-1"></i>
                              Edit
                            </button>
                          )}
                          {!isArchivedView && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleBulkStatusChange(true)}
                                title="Publish Selected Items"
                              >
                                <i className="fa fa-check me-1"></i>
                                Publish
                              </button>
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleBulkStatusChange(false)}
                                title="Unpublish Selected Items"
                              >
                                <i className="fa fa-times me-1"></i>
                                Unpublish
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={handleBulkDelete}
                                title={isBranchMode ? "Remove Selected Items From Branch" : "Archive Selected Items"}
                              >
                                <i className="fa fa-trash me-1"></i>
                                {isBranchMode ? "Remove" : "Archive"}
                              </button>
                            </>
                          )}
                          {isArchivedView && !isBranchMode && (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={handleBulkRestore}
                                title="Restore Selected Items"
                              >
                                <i className="fa fa-undo me-1"></i>
                                Restore
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={handleBulkPermanentDelete}
                                title="Permanently Delete Selected Items"
                              >
                                <i className="fa fa-trash me-1"></i>
                                Delete Permanently
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {renderNoDataMessage()}

                  {Array.isArray(items) && items.length > 0 && (
                    <>
                      <div
                        className="table-responsive table-container"
                        style={{
                          minHeight: "200px",
                          maxHeight: "450px",
                          overflowY: "auto",
                          overflowX: "auto",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <table className="table table-hover" style={{ position: "relative" }}>
                          <thead
                            className="bg-light sticky-header"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10000,
                              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                              color: isDarkMode ? "#ffffff" : "#000000",
                            }}
                          >
                            <tr>
                              <th className="pt-2 pb-2">
                                <input
                                  type="checkbox"
                                  onChange={handleSelectAll}
                                  checked={
                                    items.length > 0 &&
                                    selectedItems.size === items.length
                                  }
                                />
                              </th>
                              {visibleColumns.title && (
                                <th className="pt-2 pb-2">Title</th>
                              )}
                              {visibleColumns.sku && (
                                <th className="pt-2 pb-2">SKU</th>
                              )}

                              {visibleColumns.Price && (
                                <th className="pt-2 pb-2 text-center">
                                  Price
                                </th>
                              )}

                              {visibleColumns.deliveryPrice && (
                                <th className="pt-2 pb-2 text-center">
                                  Delivery Price
                                </th>
                              )}

                              {visibleColumns.takeAwayPrice && (
                                <th className="pt-2 pb-2 text-center">
                                  TakeAway Price
                                </th>
                              )}

                              {visibleColumns.Reporting && (
                                <th className="pt-2 pb-2">
                                  Reporting
                                </th>
                              )}

                              {visibleColumns.currentStock && (
                                <th className="pt-2 pb-2 text-center">
                                  Current Stock
                                </th>
                              )}

                              {visibleColumns.sortOrder && (
                                <th className="pt-2 pb-2 text-center">
                                  Sort Order
                                </th>
                              )}

                              {visibleColumns.rating && (
                                <th className="pt-2 pb-2 text-center">
                                  Rating
                                </th>
                              )}

                              {visibleColumns.status && (
                                <th className="pt-2 pb-2 text-center">
                                  Status
                                </th>
                              )}

                              <th className="pt-2 pb-2 text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item, index) => (
                              <tr
                                key={item.menuItem_ID}
                                draggable={false}
                                onDragStart={(e) => {
                                  // Only allow dragging from the grab icon
                                  if (!e.target.classList.contains('fa-bars')) {
                                    e.preventDefault();
                                  }
                                }}
                                onDragEnter={(e) => handleDragEnter(e, index)}
                                onDragLeave={handleDragLeave}
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                              >
                                <td
                                  className="align-middle"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedItems.has(
                                      item.menuItem_ID
                                    )}
                                    onChange={(e) =>
                                      handleSelectItem(
                                        item.menuItem_ID,
                                        index,
                                        e
                                      )
                                    }
                                    data-chkbox-shiftsel="type1"
                                  />
                                </td>

                                {visibleColumns.title && (
                                  <td
                                    className="align-middle"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_Name}
                                    {isArchivedView && (
                                      <span className="badge bg-secondary ms-2" style={{ fontSize: "10px" }}>Archived</span>
                                    )}
                                  </td>
                                )}

                                {visibleColumns.sku && (
                                  <td
                                    className="align-middle"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.menuItem_SKU}
                                  </td>
                                )}

                                {visibleColumns.Price && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_SalePrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.deliveryPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_DeliveryPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.takeAwayPrice && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {getCurrencySymbol(compCurrency)} {" "}
                                    {item.menuItem_TakeAwayPrice?.toFixed(2) || "N/A"}
                                  </td>
                                )}

                                {visibleColumns.Reporting && (
                                  <td
                                    className="align-middle"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {item.rootCat_Name}
                                  </td>
                                )}

                                {visibleColumns.currentStock && (
                                  <td
                                    className="align-middle text-center"
                                    onClick={() =>
                                      isBranchMode
                                        ? navigate(`/items/branchEditItem/${item.menuItem_ID}`)
                                        : navigate(`/items/updateItem/${item.menuItem_ID}`)
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    {(() => {
                                      const currentQty = item.current_Quantity ?? item.Current_Quantity ?? 0;
                                      const measurementType = item.menuItem_MeasurementType ?? item.MenuItem_MeasurementType ?? "";
                                      const defaultSizeUnit = item.menuItem_DefaultSizeUnit ?? item.MenuItem_DefaultSizeUnit ?? "";
                                      const qty = parseFloat(currentQty) || 0;
                                      const formattedQty = qty.toFixed(2);

                                      let unitLabel = "";
                                      if (measurementType && measurementType !== "Unit") {
                                        unitLabel = defaultSizeUnit || (measurementType === "Weight" ? "kg" : measurementType === "Volume" ? "L" : "");
                                      }

                                      return (
                                        <span>
                                          {formattedQty}
                                          {unitLabel && <span className="text-muted ms-1" style={{ fontSize: "0.9em" }}>{unitLabel}</span>}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                )}

                                {visibleColumns.sortOrder && (
                                  <td
                                    className="align-middle text-center"
                                  >
                                    {item.menuItem_Orderby}
                                  </td>
                                )}

                                {visibleColumns.rating && (
                                  <td
                                    className="align-middle text-center"
                                  >
                                    {item.menuItem_Rating}
                                  </td>
                                )}

                                {visibleColumns.status && (
                                  <td
                                    className="align-middle text-center"
                                  >
                                    {isArchivedView ? (
                                      <span className="text-muted">{item.menuItem_Publish ? "Published" : "Unpublished"}</span>
                                    ) : (
                                    <div
                                      className={`toggle-switch ${item.menuItem_Publish ? "active" : ""}`}
                                    >
                                      <label className="switch">
                                        <input
                                          type="checkbox"
                                          checked={item.menuItem_Publish}
                                          onChange={() =>
                                            toggleItemStatus(
                                              item,
                                              item.menuItem_Publish
                                            )
                                          }
                                        />
                                        <span
                                          className="slider round"
                                          style={{
                                            backgroundColor: item.menuItem_Publish ? themeColor : '#ccc'
                                          }}
                                        ></span>
                                      </label>
                                    </div>
                                    )}
                                  </td>
                                )}

                                <td className="align-middle text-center">
                                  <div className="d-flex justify-content-center gap-2">
                                    {!isBranchMode && !isArchivedView && (
                                      <>
                                        {/* Drag Handle */}
                                        <i
                                          className="fas fa-bars text-secondary"
                                          style={{
                                            cursor: "grab",
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: '1px solid #6c757d',
                                            backgroundColor: 'transparent',
                                            transition: 'all 0.2s ease'
                                          }}
                                          draggable
                                          onDragStart={(e) => handleDragStart(e, item)}
                                          onDragEnd={handleDragEnd}
                                          title="Drag to reorder"
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#6c757d';
                                            e.currentTarget.style.color = 'white';
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = '#6c757d';
                                            e.currentTarget.style.transform = 'scale(1)';
                                          }}
                                        ></i>

                                        {/* Edit Icon */}
                                        <Link
                                          to={`/items/updateItem/${item.menuItem_ID}`}
                                          className="btn btn-sm btn-outline-primary"
                                          title="Edit Item"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: `1px solid ${themeColor}`,
                                            color: themeColor,
                                            transition: 'all 0.2s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = themeColor;
                                            e.currentTarget.style.color = 'white';
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = themeColor;
                                            e.currentTarget.style.transform = 'scale(1)';
                                          }}
                                        >
                                          <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                        </Link>

                                        {/* Edit Recipe Icon - Only show for items with recipe */}
                                        {(item.MenuItem_HasRecipe || item.menuItem_HasRecipe) && (
                                          <Link
                                            to={`/recipes/createRecipe/${item.menuItem_ID}`}
                                            className="btn btn-sm btn-outline-success"
                                            title="Edit Recipe"
                                            style={{
                                              width: '32px',
                                              height: '32px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              borderRadius: '6px',
                                              border: '1px solid #28a745',
                                              color: '#28a745',
                                              transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.backgroundColor = '#28a745';
                                              e.currentTarget.style.color = 'white';
                                              e.currentTarget.style.transform = 'scale(1.05)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor = 'transparent';
                                              e.currentTarget.style.color = '#28a745';
                                              e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                          >
                                            <i className="fa fa-utensils" style={{ fontSize: '12px' }}></i>
                                          </Link>
                                        )}
                                      </>
                                    )}

                                    {!isBranchMode && isArchivedView && (
                                      <>
                                        <Link
                                          to={`/items/updateItem/${item.menuItem_ID}?includeArchived=1`}
                                          className="btn btn-sm btn-outline-secondary"
                                          title="View Item"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: '1px solid #6c757d',
                                            color: '#6c757d',
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <i className="fa fa-eye" style={{ fontSize: '12px' }}></i>
                                        </Link>
                                        <button
                                          onClick={() => handleRestoreItem(item)}
                                          className="btn btn-sm btn-outline-success"
                                          title="Restore Item"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: '1px solid #28a745',
                                            color: '#28a745',
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <i className="fa fa-undo" style={{ fontSize: '12px' }}></i>
                                        </button>
                                        <button
                                          onClick={() => handlePermanentDeleteItem(item)}
                                          className="btn btn-sm btn-outline-danger"
                                          title="Delete Permanently"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '6px',
                                            border: '1px solid #dc3545',
                                            color: '#dc3545',
                                            transition: 'all 0.2s ease'
                                          }}
                                        >
                                          <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                        </button>
                                      </>
                                    )}

                                    {isBranchMode && (
                                      <button
                                        type="button"
                                        onClick={() => navigate(`/items/branchEditItem/${item.menuItem_ID}`)}
                                        className="btn btn-sm btn-outline-primary"
                                        title="Branch edit (overrides + variants)"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          borderRadius: '6px',
                                          border: `1px solid ${themeColor}`,
                                          color: themeColor,
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = themeColor;
                                          e.currentTarget.style.color = 'white';
                                          e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                          e.currentTarget.style.color = themeColor;
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                      >
                                        <i className="fa fa-sliders-h" style={{ fontSize: '12px' }}></i>
                                      </button>
                                    )}

                                    {!isArchivedView && (
                                    <button
                                      onClick={() => handleDeleteItem(item)}
                                      className="btn btn-sm btn-outline-danger"
                                      title={isBranchMode ? "Remove From Branch" : "Archive Item"}
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '6px',
                                        border: '1px solid #dc3545',
                                        color: '#dc3545',
                                        transition: 'all 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#dc3545';
                                        e.currentTarget.style.color = 'white';
                                        e.currentTarget.style.transform = 'scale(1.05)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = '#dc3545';
                                        e.currentTarget.style.transform = 'scale(1)';
                                      }}
                                    >
                                      <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                                    </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
                <Pagination
                  totalRecords={totalRecords}
                  pageSize={pageSize}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                  setPageSize={setPageSize}
                  themeColor={themeColor}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Branch Override Modal */}
      {isBranchMode && (
        <CustomModal
          title="Branch Overrides"
          isOpen={overrideModalOpen}
          onClose={closeOverrideModal}
          isCentered={true}
          subtitle="Set branch-specific publish, sort order, and pricing overrides."
          showButton={true}
          onSubmit={saveBranchOverrides}
          submitButtonText="Save"
          submitButtonDisabled={loading}
          modalSize="modal-lg"
        >
          <div style={{ padding: 20 }}>
            {/* Product summary */}
            <div
              className="mb-4"
              style={{
                padding: 14,
                borderRadius: 14,
                backgroundColor: isDarkMode ? "#1e2124" : "#f8f9fa",
                border: `1px solid ${themeColor}20`,
              }}
            >
              <div className="fw-bold" style={{ fontSize: 14 }}>
                Product
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>
                {overrideItem?.menuItem_Name || "-"}
              </div>
              <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                Changes apply only to this branch.
              </div>
            </div>

            {/* Visibility & sorting */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-bold">Publish override</label>
                <select
                  className="form-select"
                  value={overrideForm.branchPublishMode}
                  onChange={(e) => setOverrideForm((p) => ({ ...p, branchPublishMode: e.target.value }))}
                  style={{
                    borderRadius: 12,
                    backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                    border: `1px solid ${themeColor}30`,
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <option value="inherit">Inherit (use master)</option>
                  <option value="publish">Publish</option>
                  <option value="unpublish">Unpublish</option>
                </select>
                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Keep on inherit if you want the master publish status.
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-bold">Sort order override</label>
                <input
                  type="number"
                  className="form-control"
                  value={overrideForm.branchOrderby}
                  onChange={(e) => setOverrideForm((p) => ({ ...p, branchOrderby: e.target.value }))}
                  placeholder="Leave empty to inherit"
                  style={{
                    borderRadius: 12,
                    backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                    border: `1px solid ${themeColor}30`,
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                />
                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Leave empty to use the master sort order.
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div
              className="mb-3"
              style={{
                padding: 14,
                borderRadius: 14,
                backgroundColor: isDarkMode ? "#1e2124" : "#ffffff",
                border: `1px solid ${themeColor}20`,
              }}
            >
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <div className="fw-bold" style={{ fontSize: 14 }}>
                    Pricing overrides
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Overrides apply only when enabled and value is provided.
                  </div>
                </div>

                <ToggleSwitch
                  isChecked={!!overrideForm.isPriceOverrideActive}
                  onToggle={(checked) =>
                    setOverrideForm((p) => ({ ...p, isPriceOverrideActive: checked }))
                  }
                  label="Enable"
                  name="branchPriceOverrideActive"
                  size="small"
                />
              </div>

              <div className="row g-3 mt-1">
                <div className="col-md-4">
                  <label className="form-label fw-bold">Sale price override</label>
                  <input
                    type="number"
                    className="form-control"
                    value={overrideForm.branchPriceOverride}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, branchPriceOverride: e.target.value }))}
                    placeholder="Leave empty to inherit"
                    style={{
                      borderRadius: 12,
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${themeColor}30`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">Delivery price override</label>
                  <input
                    type="number"
                    className="form-control"
                    value={overrideForm.branchDeliveryPriceOverride}
                    onChange={(e) =>
                      setOverrideForm((p) => ({ ...p, branchDeliveryPriceOverride: e.target.value }))
                    }
                    placeholder="Leave empty to inherit"
                    style={{
                      borderRadius: 12,
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${themeColor}30`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">Takeaway price override</label>
                  <input
                    type="number"
                    className="form-control"
                    value={overrideForm.branchTakeAwayPriceOverride}
                    onChange={(e) =>
                      setOverrideForm((p) => ({ ...p, branchTakeAwayPriceOverride: e.target.value }))
                    }
                    placeholder="Leave empty to inherit"
                    style={{
                      borderRadius: 12,
                      backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
                      border: `1px solid ${themeColor}30`,
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </CustomModal>
      )}

    </div>
  );
};

export default ManageItems;